/**
 * Recharge Storefront SDK — portal helpers (duftvalg + opsigelse).
 * Kræver Recharge customer portal-kontekst (/tools/recurring/*).
 */
(function () {
  'use strict';

  if (!location.pathname.includes('/tools/recurring')) return;

  var cfg = window.ScentyRechargePortal || {};
  var STORE = cfg.shopDomain || 'scenty85.shop.myshopify.com';
  var SDK_URL = 'https://static.rechargecdn.com/assets/storefront/recharge-client-1.89.0.min.js';
  var sdkPromise = null;
  var sessionPromise = null;
  var initDone = false;

  function hasRechargeClient() {
    return !!(window.recharge && window.recharge.init && window.recharge.auth && window.recharge.subscription);
  }

  function waitForRecharge(timeoutMs) {
    return new Promise(function (resolve, reject) {
      var start = Date.now();

      function check() {
        if (hasRechargeClient()) {
          resolve(window.recharge);
          return;
        }

        if (Date.now() - start > timeoutMs) {
          reject(new Error('Recharge SDK kunne ikke initialiseres'));
          return;
        }

        window.setTimeout(check, 50);
      }

      check();
    });
  }

  function loadSdk() {
    if (hasRechargeClient()) return Promise.resolve(window.recharge);
    if (sdkPromise) return sdkPromise;

    var existing = document.querySelector('script[src*="recharge-client"]');
    if (existing) {
      sdkPromise = waitForRecharge(15000);
      return sdkPromise;
    }

    sdkPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = SDK_URL;
      script.async = true;
      script.onload = function () {
        waitForRecharge(8000).then(resolve).catch(reject);
      };
      script.onerror = function () {
        reject(new Error('Recharge SDK kunne ikke indlæses'));
      };
      document.head.appendChild(script);
    });

    return sdkPromise;
  }

  function ensureInitialized(recharge) {
    if (initDone) return Promise.resolve(recharge);

    var initOpts = { storeIdentifier: STORE };
    if (cfg.storefrontAccessToken) {
      initOpts.storefrontAccessToken = cfg.storefrontAccessToken;
    }

    recharge.init(initOpts);
    initDone = true;
    return Promise.resolve(recharge);
  }

  function getSession(recharge) {
    if (sessionPromise) return sessionPromise;

    sessionPromise = loadSdk()
      .then(ensureInitialized)
      .then(function (rc) {
        return rc.auth.loginCustomerPortal();
      })
      .catch(function (err) {
        sessionPromise = null;
        throw err;
      });

    return sessionPromise;
  }

  function unwrapList(response, key) {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (response[key]) return response[key];
    if (response.data && response.data[key]) return response.data[key];
    return [];
  }

  function normalizeBundleItem(item) {
    return {
      collection_id: String(item.collection_id),
      collection_source: item.collection_source || 'shopify',
      external_product_id: String(item.external_product_id),
      external_variant_id: String(item.external_variant_id),
      quantity: item.quantity || 1
    };
  }

  function findChoiceItemIndex(items) {
    var choiceCollectionId = cfg.choiceCollectionId;
    var i;

    if (choiceCollectionId) {
      for (i = 0; i < items.length; i++) {
        if (String(items[i].collection_id) === String(choiceCollectionId)) return i;
      }
    }

    return typeof cfg.bundleChoiceSlotIndex === 'number' ? cfg.bundleChoiceSlotIndex : 0;
  }

  function getSubscriptionIdFromUrl() {
    var match = location.pathname.match(/\/subscriptions\/(\d+)/);
    return match ? match[1] : null;
  }

  function findActiveSubscription(subscriptions) {
    var urlSubId = getSubscriptionIdFromUrl();
    var i;
    var status;

    if (urlSubId) {
      for (i = 0; i < subscriptions.length; i++) {
        if (String(subscriptions[i].id) === String(urlSubId)) return subscriptions[i];
      }
    }

    for (i = 0; i < subscriptions.length; i++) {
      status = (subscriptions[i].status || '').toLowerCase();
      if (status === 'active' || status === 'onetime') return subscriptions[i];
    }

    return subscriptions.length ? subscriptions[0] : null;
  }

  function cancelActiveSubscription(options) {
    options = options || {};

    return loadSdk().then(function (recharge) {
      return getSession(recharge).then(function (session) {
        var urlSubId = getSubscriptionIdFromUrl();
        var payload = {
          cancellation_reason: options.cancellation_reason || 'Anmodet via medlemsportal',
          send_email: options.send_email !== false
        };

        if (options.cancellation_reason_comments) {
          payload.cancellation_reason_comments = options.cancellation_reason_comments;
        }

        if (urlSubId) {
          return recharge.subscription.cancelSubscription(session, urlSubId, payload);
        }

        return recharge.subscription.listSubscriptions(session, { limit: 25 }).then(function (subsResponse) {
          var subscriptions = unwrapList(subsResponse, 'subscriptions');
          var subscription = findActiveSubscription(subscriptions);

          if (!subscription) throw new Error('Intet aktivt abonnement fundet');

          return recharge.subscription.cancelSubscription(session, subscription.id, payload);
        });
      });
    });
  }

  function updateNextScent(catalogItem) {
    if (!catalogItem || !catalogItem.variant_id || !catalogItem.product_id) {
      return Promise.reject(new Error('Duft mangler variant-ID'));
    }

    return loadSdk().then(function (recharge) {
      return getSession(recharge).then(function (session) {
        return recharge.subscription.listSubscriptions(session, { limit: 25 }).then(function (subsResponse) {
          var subscriptions = unwrapList(subsResponse, 'subscriptions');
          if (!subscriptions.length) throw new Error('Intet abonnement fundet');

          var subscription = findActiveSubscription(subscriptions) || subscriptions[0];
          var purchaseItemId = subscription.id;

          return recharge.bundle.listBundleSelections(session, { limit: 50 }).then(function (selectionsResponse) {
            var selections = unwrapList(selectionsResponse, 'bundle_selections');
            var selection = null;
            var i;

            for (i = 0; i < selections.length; i++) {
              if (String(selections[i].purchase_item_id) === String(purchaseItemId)) {
                selection = selections[i];
                break;
              }
            }

            if (!selection && selections.length) selection = selections[0];
            if (!selection || !selection.items || !selection.items.length) {
              throw new Error('Kunne ikke finde dit duftvalg i abonnementet');
            }

            var items = selection.items.map(normalizeBundleItem);
            var choiceIndex = findChoiceItemIndex(items);
            items[choiceIndex].external_product_id = String(catalogItem.product_id);
            items[choiceIndex].external_variant_id = String(catalogItem.variant_id);

            return recharge.bundle.updateBundleSelection(session, selection.id, {
              purchase_item_id: purchaseItemId,
              items: items
            });
          });
        });
      });
    });
  }

  window.ScentyRechargePortalApi = {
    updateNextScent: updateNextScent,
    cancelActiveSubscription: cancelActiveSubscription
  };
})();
